// Fase 4 · UNA conexión `/hub/tpv` por dispositivo TPV en lugar de los hubs
// legacy de mesas, pedidos (restaurante + sesión), productos y reservas.
//
//  - Método único `evt` con sobre { seq, epoch, type, id, restauranteId,
//    originDeviceId, ts, data }.
//  - seqTracker: aplica en orden, ignora duplicados, rellena huecos con
//    `GET realtime/sync?since=` y, si el servidor ya no puede reponer
//    (snapshotRequired / epoch nuevo), resync completo (Fase 3 tpvResync).
//  - Al conectar: `GET realtime/seq` como punto de partida. Al reconectar o
//    volver de segundo plano, el orquestador de vuelta (tpvResume) llama a
//    `catchUp()`: reenvío de lo perdido o estado completo.
//  - `beacon` { epoch, latestSeq, ts } cada 30 s: si vamos por detrás, el
//    mismo reenvío que un hueco; epoch nuevo → resync. Tras el primer beacon
//    de cada conexión se vigila que llegue algo: Connected y 75 s sin nada
//    → zombie → se reinicia la conexión (livenessWatchdog).
import { useEffect, useRef } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { useHistory } from "react-router-dom";
import { useSnackbar } from "notistack";
import axios from "src/utils/axios";
import buildHubUrl from "src/utils/hubs/buildHubUrl";
import CreateConnection from "src/utils/hubs/HubMatrix";
import {
  addReconnectListener,
  getConnectionStates,
  markConnectionAlive,
  restartConnection,
} from "src/utils/hubs/HubConnectionManager";
import { getDeviceId } from "src/utils/deviceId";
import { bindTpvResyncStore } from "src/utils/tpvResync";
import { createConnectCatchUp, createRealtimeSync } from "./realtimeSync";
import { registerRealtimeCatchUp, requestResume } from "./tpvResume";
import { deliverReplayed } from "./legacyBridge";
import { createEventRouter } from "./eventRouter";
import { createLivenessWatchdog, LIVENESS_CHECK_MS } from "./livenessWatchdog";
import { buildTpvEventHandlers } from "./tpvEventHandlers";
import { record as recordDebug } from "src/utils/debug/debugRecorder";
import { hubInFields } from "src/utils/debug/debugEvents";

// CreateConnection notifica la conexión a Redux; aquí no hace falta guardar
// el objeto HubConnection (no serializable) en el store.
export const TPV_HUB_PATH = "/hub/tpv";

export default function useTpvRealtime() {
  const dispatch = useDispatch();
  const store = useStore();
  const history = useHistory();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const user = useSelector((state) => state.authJwt.user);

  const ctxRef = useRef(null);
  ctxRef.current = {
    dispatch,
    getState: store.getState,
    history,
    user,
    enqueueSnackbar,
    closeSnackbar,
  };

  useEffect(() => {
    bindTpvResyncStore(store.dispatch, store.getState);
  }, [store]);

  useEffect(() => {
    if (!user?.id) return undefined;

    const url = buildHubUrl(TPV_HUB_PATH, { deviceId: getDeviceId() });
    const router = createEventRouter({
      getMyDeviceId: getDeviceId,
      handlers: buildTpvEventHandlers(() => ctxRef.current),
    });

    const sync = createRealtimeSync({
      fetchSeq: async () => (await axios.get("realtime/seq"))?.data,
      fetchSync: async (since, epoch) =>
        (await axios.get("realtime/sync", { params: epoch ? { since, epoch } : { since } }))?.data,
      onApply: (env) => router.route(env),
      // Los hooks legacy puenteados también reciben lo repuesto por el
      // reenvío (el puente deduplica lo que ya le llegó en directo).
      onApplied: (env) => deliverReplayed(env),
      // Hueco imposible de reponer → la vuelta hará estado completo.
      requestSnapshot: (reason) => requestResume(`realtime-${reason}`),
      record: (kind, v) =>
        recordDebug("lifecycle", {
          actionId: null,
          actionName: null,
          eventType: kind === "gap" ? "realtime.gap" : `realtime.resync.${v}`,
          ...(kind === "gap" ? { seq: v } : {}),
        }),
    });
    const unregisterCatchUp = registerRealtimeCatchUp(() => sync.catchUp());

    // Al conectar por primera vez, ponerse al día con lo emitido entre la
    // línea base y la conexión (ver createConnectCatchUp).
    const connectCatchUp = createConnectCatchUp(sync);

    // Zombie: Connected pero sin eventos ni beacon (solo tras el 1er beacon).
    const liveness = createLivenessWatchdog({
      isConnected: () => getConnectionStates().some((c) => c.url === url && c.state === "Connected"),
      onZombie: ({ silentMs }) => {
        // eslint-disable-next-line no-console
        console.warn(`[realtime] /hub/tpv sin eventos ni beacon en ${Math.round(silentMs / 1000)} s → reinicio`);
        recordDebug("lifecycle", { actionId: null, actionName: null, eventType: "realtime.zombie" });
        restartConnection(url, "zombie");
      },
    });
    const livenessTimer = setInterval(() => liveness.check(), LIVENESS_CHECK_MS);

    const cleanup = CreateConnection(
      url,
      [
        {
          id: "evt",
          operator: (env) => {
            // Fase 5 · Cada sobre recibido → "hub_in" (solo con el monitor ON).
            recordDebug("hub_in", hubInFields(env, getDeviceId()));
            liveness.event();
            markConnectionAlive(url);
            sync.accept(env);
          },
        },
        {
          id: "beacon",
          operator: (b) => {
            liveness.beacon();
            markConnectionAlive(url);
            sync.beacon(b);
          },
        },
      ],
      dispatch,
      connectCatchUp.onConnected,
      "tpv",
    );
    connectCatchUp.baseline();

    // Reconexión (también la API reiniciada): la vuelta pone al día.
    const unsubscribeReconnect = addReconnectListener(url, () => {
      liveness.reset();
      requestResume("reconnected");
    });

    return () => {
      clearInterval(livenessTimer);
      unsubscribeReconnect();
      unregisterCatchUp();
      sync.dispose();
      cleanup();
    };
  }, [user?.id, dispatch]);
}
